import type { AxiosError } from "axios";
import { apiClient } from "shared/api/client/orval";

/* -------------------------------------------------------------------------- */
/*  Public types                                                               */
/* -------------------------------------------------------------------------- */

export type VideoUploadRole = "mainVideo" | "trailer";

export type InitVideoUploadRequest = {
  assetUploadId: number;
  role: VideoUploadRole;
  fileName: string;
  fileSizeMB: number;
  contentType: string;
};

export type InitVideoUploadResponse = {
  videoId: number;
  assetUploadId: number;
  uploadId: string;
  key: string;
  partSizeBytes: number;
};

export type PresignPartRequest = {
  videoId: number;
  uploadId: string;
  key: string;
  partNumber: number;
};

export type PresignPartResponse = {
  uploadUrl: string;
  partNumber: number;
};

export type UploadedPart = {
  partNumber: number;
  etag: string;
};

export type CompleteVideoUploadRequest = {
  videoId: number;
  uploadId: string;
  key: string;
  parts: UploadedPart[];
};

export type AbortVideoUploadRequest = {
  videoId: number;
  uploadId: string;
  key: string;
};

export type UploadProgressHandler = (
  percent: number,
  loadedBytes: number,
  totalBytes: number
) => void;

export type R2MultipartUploadOptions = {
  assetUploadId: number | string;
  role: VideoUploadRole;
  /** Defaults to `file.name`. */
  fileName?: string;
  /** Defaults to `file.type`, falling back to `application/octet-stream`. */
  contentType?: string;
  /** Parallel part uploads. Default 4, clamped to 1..8. */
  concurrency?: number;
  /** Retries per part (presign + PUT) before the whole upload fails. Default 3. */
  maxRetries?: number;
  /** Per-part PUT timeout in milliseconds. Default 10 minutes. 0 disables. */
  partTimeoutMs?: number;
  /**
   * Content-Type header to send on each part PUT. Leave undefined unless the
   * backend signs the Content-Type into the presigned URL; a mismatch makes R2
   * reject the signature.
   */
  partContentType?: string;
  signal?: AbortSignal;
  onProgress?: UploadProgressHandler;
  onPartUploaded?: (part: UploadedPart, totalParts: number) => void;
};

export type R2MultipartUploadResult = {
  videoId: number;
  assetUploadId: number;
  uploadId: string;
  key: string;
  parts: UploadedPart[];
};

export type R2MultipartUploadStage =
  | "init"
  | "presign"
  | "put"
  | "complete"
  | "abort";

export class R2MultipartUploadError extends Error {
  readonly stage: R2MultipartUploadStage;
  readonly partNumber?: number;
  readonly cause?: unknown;

  constructor(
    stage: R2MultipartUploadStage,
    message: string,
    opts: { partNumber?: number; cause?: unknown } = {}
  ) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "R2MultipartUploadError";
    this.stage = stage;
    this.partNumber = opts.partNumber;
    this.cause = opts.cause;
  }
}

export class R2MultipartUploadAbortedError extends R2MultipartUploadError {
  constructor() {
    super("abort", "Upload was cancelled.");
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "R2MultipartUploadAbortedError";
  }
}

/* -------------------------------------------------------------------------- */
/*  Constants                                                                  */
/* -------------------------------------------------------------------------- */

const DEFAULT_PART_SIZE_BYTES = 10 * 1024 * 1024;
/** R2 / S3 minimum part size (except the last part). */
const MIN_PART_SIZE_BYTES = 5 * 1024 * 1024;
/** Upper bound to keep per-part memory and retry cost sane in the browser. */
const MAX_PART_SIZE_BYTES = 100 * 1024 * 1024;
const DEFAULT_CONCURRENCY = 4;
const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_PART_TIMEOUT_MS = 10 * 60 * 1000;
/** Upper bound for the best-effort abort-video call so cleanup never hangs. */
const ABORT_TIMEOUT_MS = 5_000;
const MAX_CONCURRENCY = 8;
/** S3 / R2 multipart hard limit. */
const MAX_PARTS = 10_000;
const DEFAULT_CONTENT_TYPE = "application/octet-stream";

const ENDPOINTS = {
  init: "/api/Asset/upload/init-video",
  presignPart: "/api/Asset/upload/presign-part",
  complete: "/api/Asset/upload/complete-video",
  abort: "/api/Asset/upload/abort-video",
} as const;

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function messageFromAxiosError(err: unknown): string {
  const ax = err as AxiosError<{
    error?: string;
    message?: string;
    title?: string;
    errors?: Record<string, string[] | string>;
  }>;
  const data = ax?.response?.data;
  if (data && typeof data === "object") {
    if (typeof data.error === "string") return data.error;
    if (typeof data.title === "string") return data.title;
    if (typeof data.message === "string") return data.message;
    const nested = data.errors;
    if (nested && typeof nested === "object") {
      const first = Object.values(nested)
        .flat()
        .find((v): v is string => typeof v === "string");
      if (first) return first;
    }
  }
  if (ax?.response?.status) {
    return `Request failed with status ${ax.response.status}.`;
  }
  if (err instanceof Error) return err.message;
  return "Request failed.";
}

function isAbortError(err: unknown): boolean {
  if (err instanceof R2MultipartUploadAbortedError) return true;
  const e = err as { name?: string; code?: string };
  return e?.name === "AbortError" || e?.name === "CanceledError" || e?.code === "ERR_CANCELED";
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new R2MultipartUploadAbortedError();
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new R2MultipartUploadAbortedError());
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(new R2MultipartUploadAbortedError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

function backoffDelay(attempt: number): number {
  const base = Math.min(1000 * 2 ** attempt, 15_000);
  return base + Math.random() * 500;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function toPositiveInt(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback;
}

/**
 * The backend negotiates the part size, but we must never trust a value that
 * would make R2 reject the upload (< 5 MiB) or blow up browser memory.
 */
function normalizePartSize(value: unknown): number {
  const requested = toPositiveInt(value, DEFAULT_PART_SIZE_BYTES);
  return clamp(requested, MIN_PART_SIZE_BYTES, MAX_PART_SIZE_BYTES);
}

/** Observer callbacks must never be able to break or retry the upload engine. */
function safeInvoke(label: string, fn: () => void): void {
  try {
    fn();
  } catch (err) {
    console.error(`[r2MultipartUpload] ${label} callback threw:`, err);
  }
}

/* -------------------------------------------------------------------------- */
/*  Backend calls                                                              */
/* -------------------------------------------------------------------------- */

async function initVideoUpload(
  body: InitVideoUploadRequest,
  signal?: AbortSignal
): Promise<InitVideoUploadResponse> {
  throwIfAborted(signal);
  try {
    const normalizedRole =
      body.role === "trailer" ? "Trailer" : "MainVideo";
    // We intentionally avoid passing `signal` directly to the fast init POST
    // (with a strict 15s timeout) so that if the user cancels right as the backend
    // allocates the R2 multipart session, we still obtain the session identifiers
    // (uploadId, key, videoId) and immediately call abortVideoUpload below,
    // preventing dangling uncompleted multipart uploads on R2.
    const res = await apiClient.post<Partial<InitVideoUploadResponse>>(
      ENDPOINTS.init,
      { ...body, role: normalizedRole },
      { timeout: 15_000 }
    );
    const data = res.data ?? {};
    const videoId = Number(data.videoId);
    const assetUploadId = Number(data.assetUploadId ?? body.assetUploadId);
    const uploadId = data.uploadId;
    const key = data.key;

    if (!Number.isFinite(videoId) || !uploadId || !key) {
      throw new R2MultipartUploadError(
        "init",
        "init-video response is missing videoId, uploadId or key."
      );
    }

    if (signal?.aborted) {
      await abortVideoUpload({ videoId, uploadId, key });
      throw new R2MultipartUploadAbortedError();
    }

    return {
      videoId,
      assetUploadId,
      uploadId,
      key,
      partSizeBytes: normalizePartSize(data.partSizeBytes),
    };
  } catch (err) {
    if (err instanceof R2MultipartUploadError) throw err;
    if (isAbortError(err)) {
      throw new R2MultipartUploadAbortedError();
    }
    throw new R2MultipartUploadError("init", messageFromAxiosError(err), { cause: err });
  }
}

async function presignPart(
  body: PresignPartRequest,
  signal?: AbortSignal
): Promise<string> {
  try {
    const res = await apiClient.post<Partial<PresignPartResponse> | string>(
      ENDPOINTS.presignPart,
      body,
      { signal }
    );
    const data = res.data as any;
    const url =
      typeof res.data === "string" ? res.data : (data?.uploadUrl || data?.url);
    if (!url) {
      throw new R2MultipartUploadError(
        "presign",
        `presign-part returned no URL for part ${body.partNumber}.`,
        { partNumber: body.partNumber }
      );
    }
    return url;
  } catch (err) {
    if (err instanceof R2MultipartUploadError) throw err;
    if (isAbortError(err)) throw new R2MultipartUploadAbortedError();
    throw new R2MultipartUploadError("presign", messageFromAxiosError(err), {
      partNumber: body.partNumber,
      cause: err,
    });
  }
}

async function completeVideoUpload(
  body: CompleteVideoUploadRequest,
  signal?: AbortSignal
): Promise<void> {
  try {
    await apiClient.post(ENDPOINTS.complete, body, { signal });
  } catch (err) {
    if (isAbortError(err)) throw new R2MultipartUploadAbortedError();
    throw new R2MultipartUploadError("complete", messageFromAxiosError(err), { cause: err });
  }
}

/**
 * Best effort — never throws. Deliberately ignores the caller's abort signal,
 * but is bounded by its own timeout so cleanup can never hang the caller.
 */
async function abortVideoUpload(body: AbortVideoUploadRequest): Promise<void> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ABORT_TIMEOUT_MS);
  try {
    await apiClient.post(ENDPOINTS.abort, body, {
      signal: controller.signal,
      timeout: ABORT_TIMEOUT_MS,
    });
  } catch {
    /* swallow: the original error is what the caller should see */
  } finally {
    clearTimeout(timer);
  }
}

/* -------------------------------------------------------------------------- */
/*  Direct-to-R2 PUT                                                           */
/* -------------------------------------------------------------------------- */

type PutPartParams = {
  url: string;
  chunk: Blob;
  partNumber: number;
  timeoutMs: number;
  contentType?: string;
  signal?: AbortSignal;
  onProgress: (loadedBytes: number) => void;
};

/**
 * Uses XMLHttpRequest instead of fetch to get upload progress events.
 * Requires the R2 bucket CORS policy to include `ExposeHeaders: ["ETag"]`.
 */
function putPart({
  url,
  chunk,
  partNumber,
  timeoutMs,
  contentType,
  signal,
  onProgress,
}: PutPartParams): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new R2MultipartUploadAbortedError());
      return;
    }

    const xhr = new XMLHttpRequest();
    const fail = (err: R2MultipartUploadError) => {
      cleanup();
      reject(err);
    };
    const onAbort = () => {
      xhr.abort();
      fail(new R2MultipartUploadAbortedError());
    };
    const cleanup = () => signal?.removeEventListener("abort", onAbort);

    xhr.open("PUT", url, true);
    xhr.timeout = timeoutMs;
    if (contentType) xhr.setRequestHeader("Content-Type", contentType);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded);
    };

    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        fail(
          new R2MultipartUploadError(
            "put",
            `R2 rejected part ${partNumber} with status ${xhr.status}.`,
            { partNumber }
          )
        );
        return;
      }
      const etag = xhr.getResponseHeader("ETag");
      if (!etag) {
        fail(
          new R2MultipartUploadError(
            "put",
            `No ETag header on part ${partNumber}. Ensure the R2 bucket CORS policy exposes "ETag".`,
            { partNumber }
          )
        );
        return;
      }
      onProgress(chunk.size);
      cleanup();
      resolve(etag);
    };

    xhr.onerror = () =>
      fail(
        new R2MultipartUploadError("put", `Network error uploading part ${partNumber}.`, {
          partNumber,
        })
      );
    xhr.ontimeout = () =>
      fail(
        new R2MultipartUploadError("put", `Timed out uploading part ${partNumber}.`, {
          partNumber,
        })
      );
    xhr.onabort = () => {
      if (!signal?.aborted) {
        fail(
          new R2MultipartUploadError("put", `Upload of part ${partNumber} was aborted.`, {
            partNumber,
          })
        );
      }
    };

    signal?.addEventListener("abort", onAbort, { once: true });
    xhr.send(chunk);
  });
}

/* -------------------------------------------------------------------------- */
/*  Orchestration                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Uploads a (potentially very large) video file straight to Cloudflare R2
 * using S3-style multipart upload with backend-issued presigned part URLs.
 *
 * Flow: init-video → [presign-part → PUT] × N (parallel) → complete-video.
 * On cancellation or any unrecoverable failure, abort-video is called so R2
 * does not keep orphaned parts.
 */
export async function uploadVideoToR2(
  file: File,
  options: R2MultipartUploadOptions
): Promise<R2MultipartUploadResult> {
  const {
    role,
    signal,
    onProgress,
    onPartUploaded,
    partContentType,
    fileName = file.name,
    contentType = file.type || DEFAULT_CONTENT_TYPE,
    concurrency = DEFAULT_CONCURRENCY,
    maxRetries = DEFAULT_MAX_RETRIES,
    partTimeoutMs = DEFAULT_PART_TIMEOUT_MS,
  } = options;

  if (!(file instanceof Blob) || file.size <= 0) {
    throw new R2MultipartUploadError("init", "File is empty or invalid.");
  }

  const assetUploadId = Number(options.assetUploadId);
  if (!Number.isFinite(assetUploadId)) {
    throw new R2MultipartUploadError("init", "assetUploadId must be numeric.");
  }

  throwIfAborted(signal);

  // Internal controller: fans out to every worker so that a single part's
  // unrecoverable failure (or the caller's abort) tears down all siblings at
  // once instead of letting them burn bandwidth on a doomed upload.
  const internal = new AbortController();
  const internalSignal = internal.signal;
  const onExternalAbort = () => internal.abort();
  signal?.addEventListener("abort", onExternalAbort, { once: true });

  try {
    return await runMultipartUpload(file, {
      role,
      fileName,
      contentType,
      concurrency,
      maxRetries,
      partTimeoutMs,
      partContentType,
      assetUploadId,
      externalSignal: signal,
      signal: internalSignal,
      abortInternal: () => internal.abort(),
      onProgress,
      onPartUploaded,
    });
  } finally {
    signal?.removeEventListener("abort", onExternalAbort);
  }
}

type RunMultipartUploadParams = {
  role: VideoUploadRole;
  fileName: string;
  contentType: string;
  concurrency: number;
  maxRetries: number;
  partTimeoutMs: number;
  partContentType?: string;
  assetUploadId: number;
  /** The caller's signal; used to distinguish user cancellation from internal teardown. */
  externalSignal?: AbortSignal;
  /** Linked internal signal handed to every network call and worker. */
  signal: AbortSignal;
  abortInternal: () => void;
  onProgress?: UploadProgressHandler;
  onPartUploaded?: (part: UploadedPart, totalParts: number) => void;
};

async function runMultipartUpload(
  file: File,
  {
    role,
    fileName,
    contentType,
    concurrency,
    maxRetries,
    partTimeoutMs,
    partContentType,
    assetUploadId,
    externalSignal,
    signal,
    abortInternal,
    onProgress,
    onPartUploaded,
  }: RunMultipartUploadParams
): Promise<R2MultipartUploadResult> {
  const totalBytes = file.size;
  const session = await initVideoUpload(
    {
      assetUploadId,
      role,
      fileName,
      fileSizeMB: Math.ceil(totalBytes / (1024 * 1024)),
      contentType,
    },
    signal
  );

  const { videoId, uploadId, key, partSizeBytes } = session;
  const abortBody: AbortVideoUploadRequest = { videoId, uploadId, key };

  if (externalSignal?.aborted) {
    await abortVideoUpload(abortBody);
    throw new R2MultipartUploadAbortedError();
  }

  const totalParts = Math.ceil(totalBytes / partSizeBytes);

  if (totalParts > MAX_PARTS) {
    await abortVideoUpload(abortBody);
    throw new R2MultipartUploadError(
      "init",
      `File needs ${totalParts} parts of ${partSizeBytes} bytes, exceeding the ${MAX_PARTS}-part limit.`
    );
  }

  const workerCount = clamp(Math.floor(concurrency), 1, Math.min(MAX_CONCURRENCY, totalParts));
  const loadedPerPart = new Array<number>(totalParts).fill(0);
  const completedParts: UploadedPart[] = [];

  const emitProgress = () => {
    if (!onProgress) return;
    let loaded = 0;
    for (let i = 0; i < loadedPerPart.length; i++) loaded += loadedPerPart[i];
    loaded = Math.min(loaded, totalBytes);
    const percent = Math.round((loaded / totalBytes) * 10_000) / 100;
    safeInvoke("onProgress", () => onProgress(percent, loaded, totalBytes));
  };

  const uploadPart = async (partIndex: number): Promise<void> => {
    const partNumber = partIndex + 1;
    const start = partIndex * partSizeBytes;
    const end = Math.min(start + partSizeBytes, totalBytes);
    const expectedPartBytes = end - start;
    const chunk = file.slice(start, end);

    for (let attempt = 0; ; attempt++) {
      throwIfAborted(signal);
      try {
        // Presign inside the retry loop: URLs expire and a stale one fails the PUT.
        const url = await presignPart({ videoId, uploadId, key, partNumber }, signal);
        const etag = await putPart({
          url,
          chunk,
          partNumber,
          timeoutMs: partTimeoutMs,
          contentType: partContentType,
          signal,
          onProgress: (loaded) => {
            loadedPerPart[partIndex] = clamp(loaded, 0, expectedPartBytes);
            emitProgress();
          },
        });

        if (loadedPerPart[partIndex] !== expectedPartBytes) {
          loadedPerPart[partIndex] = expectedPartBytes;
          emitProgress();
        }

        const part: UploadedPart = { partNumber, etag };
        completedParts.push(part);
        if (onPartUploaded) {
          safeInvoke("onPartUploaded", () => onPartUploaded(part, totalParts));
        }
        return;
      } catch (err) {
        if (isAbortError(err) || attempt >= maxRetries) throw err;
        loadedPerPart[partIndex] = 0;
        emitProgress();
        await sleep(backoffDelay(attempt), signal);
      }
    }
  };

  let nextPartIndex = 0;
  let failure: unknown = null;

  const runWorker = async (): Promise<void> => {
    while (failure === null) {
      const partIndex = nextPartIndex++;
      if (partIndex >= totalParts) return;
      try {
        await uploadPart(partIndex);
      } catch (err) {
        if (failure === null) {
          failure = err;
          // Tear down sibling workers immediately; they would only fail later.
          abortInternal();
        }
        return;
      }
    }
  };

  emitProgress();
  await Promise.all(Array.from({ length: workerCount }, runWorker));

  if (failure !== null) {
    await abortVideoUpload(abortBody);
    throw isAbortError(failure) ? new R2MultipartUploadAbortedError() : failure;
  }

  const parts = [...completedParts].sort((a, b) => a.partNumber - b.partNumber);

  try {
    throwIfAborted(externalSignal);
    await completeVideoUpload({ videoId, uploadId, key, parts }, signal);
  } catch (err) {
    await abortVideoUpload(abortBody);
    throw isAbortError(err) ? new R2MultipartUploadAbortedError() : err;
  }

  if (onProgress) {
    safeInvoke("onProgress", () => onProgress(100, totalBytes, totalBytes));
  }

  return {
    videoId,
    assetUploadId: session.assetUploadId,
    uploadId,
    key,
    parts,
  };
}
